/**
 * Issue-only health rail (S22) — a thin bar above the grid with one chip per
 * live issue (`1 stale`, `2 slow`, `1 alert`). Renders NOTHING when the
 * dashboard is healthy: no settings, no chrome, zero footprint for users who
 * never hit an issue. Derived purely from `services/healthSummary.ts` fed by the S3
 * runtime store + the S12 alert store — this component owns the store
 * subscriptions and DOM-level "spotlight" side effect; the summary math
 * itself is pure and unit-tested separately.
 *
 * Clicking a segment scrolls the first affected tile into view and pulses an
 * outline (`.animate-tile-spotlight-pulse`, `src/index.css`) on every
 * affected tile — done via direct DOM lookup scoped to the enclosing
 * dashboard tab (`[data-testid="dashboard-tab"]`) rather than plumbing a
 * spotlight prop through `DashboardGrid`/`TileFrame`, so this slice stays a
 * single, additive component with no shared-file edits to the grid.
 *
 * Segments are CHIPS, not prose: the rail reads as a row of controls because
 * every segment is clickable (UX review — the old dot-separated sentence read
 * as static text). The rail's one bulk action — "Refresh stale" — sits at the
 * right edge and re-runs every stale tile through the SAME manual-run path the
 * tile kebab's "Refresh now" uses (`services/manualTileRun.ts`), sequentially
 * so a write-classified tile's destructive-confirm dialog (one-at-a-time host)
 * can't be clobbered by the next tile's.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';
import { tileRuntimeKey, useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';
import { useDashboardAlertStore } from '../store/dashboardAlertStore';
import { runTileManually } from '../services/manualTileRun';
import {
  computeHealthSummary,
  segmentTileIds,
  type HealthSegment,
} from '../services/healthSummary';

const SPOTLIGHT_CLASS = 'animate-tile-spotlight-pulse';
const SPOTLIGHT_DURATION_MS = 1300;

function spotlightTiles(scope: ParentNode, tileIds: string[]): void {
  let first: HTMLElement | null = null;
  for (const tileId of tileIds) {
    const el = scope.querySelector<HTMLElement>(
      `[data-testid="dashboard-tile-wrapper"][data-tile-id="${CSS.escape(tileId)}"]`,
    );
    if (!el) continue;
    first ??= el;
    // Restart the animation even if this tile was just spotlighted (clicking
    // the same segment twice in a row should re-pulse, not no-op).
    el.classList.remove(SPOTLIGHT_CLASS);
    void el.offsetWidth; // force reflow so the class re-add restarts the keyframe
    el.classList.add(SPOTLIGHT_CLASS);
    window.setTimeout(() => el.classList.remove(SPOTLIGHT_CLASS), SPOTLIGHT_DURATION_MS);
  }
  first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/** Chip styling for the clickable segments — obviously interactive (border +
 * wash + pointer + focus ring), unlike the original inline text run. */
const SEGMENT_CHIP_CLASS =
  'inline-flex cursor-pointer items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-foreground transition-colors hover:border-amber-500/70 hover:bg-amber-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-amber-400/40 dark:bg-amber-400/10 dark:hover:border-amber-300/70 dark:hover:bg-amber-400/25';

export function HealthRail({ dashboardId, tiles }: { dashboardId: string; tiles: DashboardTile[] }) {
  const { track } = useAnalytics();
  const rootRef = useRef<HTMLDivElement>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Re-armed in the effect BODY, not just initialized: StrictMode's
  // mount→unmount→remount would otherwise leave it false and strand the
  // button in its "refreshing" state forever.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const byTile = useDashboardRuntimeStore((s) => s.byTile);
  const alertMap = useDashboardAlertStore((s) => s.byDashboard[dashboardId]);
  const ensureAlertsLoaded = useDashboardAlertStore((s) => s.ensureLoaded);

  useEffect(() => {
    void ensureAlertsLoaded(dashboardId);
  }, [dashboardId, ensureAlertsLoaded]);

  const summary = useMemo(() => {
    const inputs = tiles.filter(isQueryBackedTile).map((tile) => {
      const runtime = byTile[tileRuntimeKey(dashboardId, tile.id)];
      return {
        tileId: tile.id,
        runtimeStatus: runtime?.status,
        durationsMs: runtime?.durationsMs,
        alertStatus: alertMap?.[tile.id]?.status,
      };
    });
    return computeHealthSummary(inputs);
  }, [tiles, byTile, alertMap, dashboardId]);

  // The stale segment's tiles as full definitions — the bulk action needs the
  // tile objects, the summary only carries ids.
  const staleTiles = useMemo(() => {
    const ids = new Set(segmentTileIds(summary, 'stale'));
    return ids.size === 0 ? [] : tiles.filter(isQueryBackedTile).filter((t) => ids.has(t.id));
  }, [summary, tiles]);

  if (summary.isHealthy) return null;

  const handleClick = (segment: HealthSegment, tileIds: string[]) => {
    track('feature:dashboard_health_rail_click', { segment });
    const scope = rootRef.current?.closest<HTMLElement>('[data-testid="dashboard-tab"]');
    if (scope) spotlightTiles(scope, tileIds);
  };

  const handleRefreshStale = async () => {
    if (refreshing || staleTiles.length === 0) return;
    track('feature:dashboard_health_rail_refresh_stale', { tiles: staleTiles.length });
    setRefreshing(true);
    try {
      // Sequential on purpose: a manual run can raise the destructive-confirm
      // dialog, and `TileDangerousConfirmHost` holds ONE pending confirmation —
      // firing them in parallel would strand every run but the last.
      for (const tile of staleTiles) {
        await runTileManually(dashboardId, tile).catch(() => undefined);
      }
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  };

  return (
    <div
      ref={rootRef}
      role="status"
      className="flex items-center gap-1.5 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1 text-xs text-muted-foreground dark:border-amber-400/30 dark:bg-amber-400/15"
      data-testid="dashboard-health-rail"
    >
      <TriangleAlert
        className="h-3 w-3 shrink-0 text-warning"
        aria-hidden
      />
      {summary.segments.map((s) => (
        <button
          key={s.segment}
          type="button"
          onClick={() => handleClick(s.segment, s.tileIds)}
          className={SEGMENT_CHIP_CLASS}
          data-testid={`health-rail-segment-${s.segment}`}
          title={`Spotlight the ${s.count} ${s.segment} tile${s.count === 1 ? '' : 's'}`}
        >
          {s.count} {s.segment}
        </button>
      ))}
      <span className="flex-1" />
      {staleTiles.length > 0 && (
        <button
          type="button"
          onClick={() => void handleRefreshStale()}
          disabled={refreshing}
          className="inline-flex cursor-pointer items-center gap-1 rounded-sm px-1.5 py-0.5 font-medium text-amber-700 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-default disabled:opacity-60 dark:text-amber-300 dark:hover:bg-amber-400/20"
          data-testid="health-rail-refresh-stale"
          title={`Re-run ${staleTiles.length} stale tile${staleTiles.length === 1 ? '' : 's'} now`}
        >
          {refreshing ? (
            <RandomDotmSpinner size={12} />
          ) : (
            <RefreshCw className="h-3 w-3" aria-hidden />
          )}
          Refresh stale
        </button>
      )}
    </div>
  );
}
